// Navigation moves the URL before the server step lands. Until it does, a page renders from the
// last step that landed on it: its moment picks the sections, and its intents build the actions.
// Item-scoped steps are kept per item, because their intents are filtered by the selected item.
// They are dropped with the query cache, so they never outlive the session and customer they came from,
// and after an action refreshes the portal data, so a page never lays out from before the action.
import { PORTAL_PAGES, InputType } from '@/constants'
import { useSessionStore } from '@/stores'
import { onPortalQueriesRefreshed, onQueryCacheCleared } from '@/services/queryClient'
import type { ConfigurablePage, MomentKey } from '@/types'
import { resolveMoment } from './sectionsConfig'
import { inputTypeForPath, itemIdFromPath } from './portalNav'
import { isPortalStep } from './portalActions'

const STEP_FOR_PAGE: Partial<Record<ConfigurablePage, number>> = {
  [PORTAL_PAGES.Home]: InputType.CustomerPortalHome,
  [PORTAL_PAGES.SubscriptionDetails]: InputType.CustomerPortalSubscriptionDetail,
  [PORTAL_PAGES.OrderDetails]: InputType.CustomerPortalOrderDetail,
}

const snapshots = new Map<string, any>()
function clearSnapshots(): void {
  snapshots.clear()
}

function itemKey(stepType: number, itemId: number | string): string {
  return `${stepType}:${itemId}`
}

function rememberStep(session: any): void {
  if (!session) {
    clearSnapshots()
    return
  }
  if (!isPortalStep(session) || session.is_popup) {
    return
  }
  const stepType = Number(session.step_type)
  snapshots.set(String(stepType), session)
  const itemId = session.selected_item?.id
  if (itemId != null) {
    snapshots.set(itemKey(stepType, itemId), session)
  }
}

export function stepSnapshotForPath(path: string): any | undefined {
  const stepType = inputTypeForPath(path)
  if (stepType === undefined) {
    return undefined
  }
  const itemId = itemIdFromPath(path)
  return snapshots.get(itemId === undefined ? String(stepType) : itemKey(stepType, itemId))
}

export function rememberedPageMoment(page: ConfigurablePage): MomentKey | undefined {
  const stepType = STEP_FOR_PAGE[page]
  const snapshot = stepType === undefined ? undefined : snapshots.get(String(stepType))
  return snapshot ? resolveMoment(snapshot) : undefined
}

export function expectedPageMoment(page: ConfigurablePage, pathname: string): MomentKey | undefined {
  const snapshot = stepSnapshotForPath(pathname)
  return snapshot ? resolveMoment(snapshot) : rememberedPageMoment(page)
}

export function trackStepSnapshots(): () => void {
  clearSnapshots()
  rememberStep(useSessionStore.getState().session)
  const unsubscribeSession = useSessionStore.subscribe((state, prev) => {
    if (state.session !== prev.session) {
      rememberStep(state.session)
    }
  })
  const unsubscribeCache = onQueryCacheCleared(clearSnapshots)
  const unsubscribeRefresh = onPortalQueriesRefreshed(() => {
    clearSnapshots()
    rememberStep(useSessionStore.getState().session)
  })
  return () => {
    unsubscribeSession()
    unsubscribeCache()
    unsubscribeRefresh()
    clearSnapshots()
  }
}
